import React, { useState } from "react";
import PropTypes from "prop-types";
import { withFormik } from "formik";
import { compose } from "redux";
import { withRouter } from "react-router";

// app
import { titleCase, validateEmail } from "../../utils";
import mapAddressToFormValues from "../../utils/mapAddressToFormValues";
import VerifyAddressForm from "../../components/VerifyAddressForm";
import VerifyPropertyBrief from "../../components/VerifyPropertyBrief";
import { AssessmentCodeModal, TermsAndConditionsModal } from "../../components/Modals";
import { isPreOrderFlow } from "../../constants";
import { yearBuiltMaxPlausible } from "../../lib/dateUtils";
import {
  EmailGroup,
  CertifyAndSubmit,
  DiscountInline,
  ReassuranceNote,
  AppliedCodeChip,
  VerifyHeadingSlot,
} from "./styled";

const CURRENT_STEP = 1;

const VerifyAddress = ({
  selectedAddress,
  handleChange,
  setFieldValue,
  values,
  errors,
  handleSubmit,
  isSubmitting,
  onSubmit,
  selectAddress,
  clearSuggestions,
  setAssessmentFormValues,
}) => {
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  // discountObject lives in formik values so the config-level
  // handleSubmit can pass it to postAssessmentData (handleSubmit
  // there only sees props + values, not local state).
  const discountObject = values.discountObject;
  const setDiscountObject = (d) => setFieldValue("discountObject", d);

  const onChangeCity = (e) => {
    const titleCaseValue = titleCase(e.target.value);
    handleChange(e, { newValue: titleCaseValue });
  };

  const goBack = () => {
    onSubmit(CURRENT_STEP - 1);
    selectAddress({});
    setAssessmentFormValues({});
    clearSuggestions();
  };

  const handleEmailChange = (e) => {
    setFieldValue("customerEmail", e.target.value);
  };

  const handleTermsChange = () => {
    setFieldValue("isInformationCertified", !values.isInformationCertified);
  };

  const handlePrimaryResidenceChange = () => {
    setFieldValue("isPrimaryResidence", !values.isPrimaryResidence);
  };

  // Discount code modal hook — when the customer submits a code we
  // pull the comparison fields the modal forwards from the validate
  // response (matches the SelectReportPage flow).
  const onSubmitCode = (discount) => {
    setDiscountObject(discount);
  };

  const removeDiscount = () => setDiscountObject(null);

  // Final-submit gating: every property field is non-error,
  // email is valid, terms checked.
  const propertyFieldsValid = Object.keys(errors).every(
    (k) => k === "customerEmail" || k === "isInformationCertified" || !errors[k],
  );
  const emailOk = !!values.customerEmail && validateEmail(values.customerEmail);
  const canSubmit =
    propertyFieldsValid && emailOk && !!values.isInformationCertified && !isSubmitting;

  // The verify panel is laid out so the form reads horizontally
  // rather than as a tall vertical stack:
  //
  //   ┌─heading slot (eyebrow + heading + back link)──────────┐
  //   │ Address (full width)                                  │
  //   │ ┌─Left rail──────────┬─Right rail─────────────────┐  │
  //   │ │ Property facts     │ Map                        │  │
  //   │ │ inline checkbox    │ Reassurance note           │  │
  //   │ │ + discount link    │                            │  │
  //   │ │ Email field        │                            │  │
  //   │ │ T&Cs check         │                            │  │
  //   │ └────────────────────┴────────────────────────────┘  │
  //   │ Submit button (full width)                            │
  //   └───────────────────────────────────────────────────────┘
  //
  // Email + T&Cs sit in the LEFT rail under the property facts
  // (paired with the data they qualify); reassurance sits in the
  // RIGHT rail under the map (balances the column heights).  No
  // more full-width stack of mini-cards under a half-width body.
  const headingSlot = (
    <VerifyHeadingSlot>
      <div className="step-eyebrow">
        <span className="pip">→</span>
        Final step · Confirm to continue
      </div>
      <h2 className="step-heading">
        Confirm the property details — and we'll email your
        Free Assessment
      </h2>
      <p className="step-blurb">
        Quick double-check on the facts the appraisal district has on
        your home. The numbers above use these exact details — update
        anything that's off.{" "}
        <button
          type="button"
          className="step-back-link"
          onClick={goBack}
        >
          Wrong address? Search again →
        </button>
      </p>
    </VerifyHeadingSlot>
  );

  const leftRailFooter = (
    <>
      <DiscountInline>
        <label className="primary-residence-inline">
          <input
            type="checkbox"
            checked={!!values.isPrimaryResidence}
            onChange={handlePrimaryResidenceChange}
          />{" "}
          This is my primary residence
        </label>
        <span className="divider" aria-hidden>·</span>
        {discountObject ? (
          <AppliedCodeChip>
            <span className="dot" aria-hidden>✓</span>
            Code <strong>{discountObject.value}</strong> applied
            <button
              type="button"
              onClick={removeDiscount}
              aria-label="Remove discount code"
            >
              remove
            </button>
          </AppliedCodeChip>
        ) : (
          <button
            type="button"
            className="discount-quiet-link"
            onClick={() => setCodeModalOpen(true)}
          >
            Have a discount or pre-order code?
          </button>
        )}
      </DiscountInline>

      <EmailGroup>
        <label htmlFor="verify-email">
          What email should we send your free property assessment to?
        </label>
        <input
          id="verify-email"
          type="email"
          autoComplete="email"
          value={values.customerEmail}
          onChange={handleEmailChange}
          placeholder="you@example.com"
          aria-invalid={values.customerEmail && !emailOk}
        />
        <small>
          We'll email you within a few hours — check your inbox or spam folder.
        </small>
      </EmailGroup>

      <CertifyAndSubmit>
        <label className="check-row terms">
          <input
            type="checkbox"
            checked={!!values.isInformationCertified}
            onChange={handleTermsChange}
          />{" "}
          I confirm the property details above are accurate and accept the{" "}
          <button
            type="button"
            className="link"
            onClick={() => setTermsModalOpen(true)}
          >
            terms and conditions
          </button>
          .
        </label>
      </CertifyAndSubmit>
    </>
  );

  const rightRailFooter = (
    <ReassuranceNote>
      <span aria-hidden="true">📬</span>
      <span>
        We'll email your free assessment within a few hours. Be sure to
        check your inbox or spam folder.
      </span>
    </ReassuranceNote>
  );

  return (
    <>
      <VerifyPropertyBrief selectedAddress={selectedAddress} />
      <VerifyAddressForm
        goBack={goBack}
        values={values}
        selectedAddress={selectedAddress}
        errors={errors}
        handleChange={handleChange}
        handleCertifyInformationChange={() => {}}
        isInformationCertified={!!values.isInformationCertified}
        handlePrimaryResidenceChange={handlePrimaryResidenceChange}
        isPrimaryResidence={!!values.isPrimaryResidence}
        onChangeCity={onChangeCity}
        handleSubmit={handleSubmit}
        submitButtonText="Save and see my property dossier"
        submitButtonDisabled={!canSubmit}
        disableConfirmation
        headingSlot={headingSlot}
        belowFieldsAddon={leftRailFooter}
        belowMapAddon={rightRailFooter}
      />
      <AssessmentCodeModal
        isOpen={codeModalOpen}
        toggle={() => setCodeModalOpen((p) => !p)}
        submitCode={(d) => {
          onSubmitCode(d);
          setCodeModalOpen(false);
        }}
      />
      <TermsAndConditionsModal
        isOpen={termsModalOpen}
        toggle={() => setTermsModalOpen((p) => !p)}
      />
    </>
  );
};

VerifyAddress.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  selectedAddress: PropTypes.object,
};

// Plausibility ranges for customer-submitted property overrides.  Kept in
// lockstep with the server-side clamp in
// texas-taxes-api/taxes/apiserver/viewsets/property_viewset.py — if these
// drift, the API will 400 a form the UI accepted and the user is left
// guessing.  Any field outside the band must be resolved by a human, so
// the message funnels customers to support instead of silently clipping.
const OUT_OF_RANGE_MSG =
  "That value is outside our normal range. If you believe this is correct, please contact support@… and we'll set it manually.";

const RANGES = {
  bedCount:      { min: 0, max: 15, integer: true },
  bathCount:     { min: 0, max: 15 },
  buildingSqFt:  { min: 100, max: 50000 },
  lotSqFt:       { min: 0, max: 10_000_000 },
  // yearBuilt upper bound is dynamic (current year + 1) — applied below.
  yearBuilt:     { min: 1700 },
};

// Field whitelist — anything outside this set (e.g. our custom
// customerEmail / isInformationCertified / isPrimaryResidence /
// discountObject formik values) is skipped during property-fact
// validation so we don't return spurious "missing value" errors
// for the consolidated-form additions.
const PROPERTY_FIELDS = new Set(Object.keys(RANGES).concat(["city"]));

export const validationFunc = (values) => {
  const errors = {};
  const yearMax = yearBuiltMaxPlausible();

  Object.keys(values).forEach((key) => {
    if (!PROPERTY_FIELDS.has(key)) return;
    if (values[key] === "" || values[key] === null || values[key] === undefined) {
      errors[key] = "We need to know this value";
      return;
    }

    const range = RANGES[key];
    if (!range) return;

    const numeric = +values[key];
    if (Number.isNaN(numeric)) {
      errors[key] = "Please enter a number";
      return;
    }
    if (range.integer && !Number.isInteger(numeric)) {
      errors[key] = "This field needs to be an integer";
      return;
    }

    const max = key === "yearBuilt" ? yearMax : range.max;
    if (numeric < range.min || numeric > max) {
      errors[key] = OUT_OF_RANGE_MSG;
    }
  });

  return errors;
};

const CreateVerifyFormik = withFormik({
  mapPropsToValues: (props) => ({
    ...mapAddressToFormValues(props),
    // ``isPrimaryResidence`` defaults to true for the typical
    // homeowner-visiting-our-site case; only the under-$200k
    // free-report path actually surfaces the question.
    isPrimaryResidence: true,
    // Email defaults to whatever's already in redux (customer
    // resuming a flow) so we don't make them retype it.
    customerEmail: props.customerEmail || "",
    isInformationCertified: false,
    // Stored as a formik value (rather than local useState) so
    // the config-level handleSubmit can read it when posting.
    discountObject: null,
  }),

  validate: (values) => {
    return validationFunc(values);
  },

  mapPropsToErrors: (props) => {
    const values = mapAddressToFormValues(props);
    return validationFunc(values);
  },

  handleSubmit: async (
    {
      bedCount,
      bathCount,
      buildingSqFt,
      lotSqFt,
      yearBuilt,
      city,
      isPrimaryResidence,
      customerEmail,
      discountObject,
    },
    { props, setSubmitting },
  ) => {
    const modifiedAddress = {
      bed_count: bedCount,
      bath_count: bathCount,
      building_sq_ft: buildingSqFt,
      lot_sq_ft: lotSqFt,
      year_built: yearBuilt,
      city,
      is_primary_residence: !!isPrimaryResidence,
    };

    // Persist form values + email into redux so postAssessmentData
    // (which reads from redux state, not its arguments) picks them
    // up when it builds the begin-computation payload.
    props.setAssessmentFormValues(modifiedAddress);
    props.setUserEmail(customerEmail);

    if (isPreOrderFlow) {
      // Pre-order mode keeps its own multi-step confirmation flow
      // — fall back to the legacy step-bump that surfaces it.
      props.onSubmit(CURRENT_STEP + 1);
      return;
    }

    setSubmitting(true);
    try {
      const res = await props.postAssessmentData(discountObject || null, false);
      if (res && res.data && res.data.uuid) {
        props.history.push(`/property-dossier/${res.data.uuid}`);
        return;
      }
      // postAssessmentData resolved without a uuid — error path
      // already dispatched a notification, just unblock the form.
      setSubmitting(false);
    } catch (_e) {
      setSubmitting(false);
    }
  },
});

export default compose(withRouter, CreateVerifyFormik)(VerifyAddress);
